import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import type { Repeat, Weekday } from "../types";
import { WEEKDAY_NAMES, withEvery } from "../views/scheduleText";

const MONTH_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

/** A slot as the Schedules page shows it, in the viewer's locale. */
export function when(ts: number | null): string {
	if (ts === null) return "—";
	return new Date(ts).toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}

/**
 * A repeat's frequency, day and time, shared by the Schedules page form and the schedule card. The next
 * three slots come from the server, so they are in the time zone the scheduler uses; none are fetched
 * while disabled.
 */
export function RepeatField({ repeat, onChange, disabled = false }: { repeat: Repeat; onChange: (repeat: Repeat) => void; disabled?: boolean }) {
	const [preview, setPreview] = useState<{ timeZone: string; next: number[] } | { error: string } | null>(null);
	const key = JSON.stringify(repeat);
	const slots = disabled ? null : preview;

	useEffect(() => {
		if (disabled) return;
		const timer = setTimeout(() => {
			api
				.previewRepeat(repeat)
				.then(setPreview)
				.catch((err) => setPreview({ error: err instanceof ApiError ? err.message : "时间无效" }));
		}, 300);
		return () => clearTimeout(timer);
		// `key` stands for `repeat`, whose identity may change on every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key, disabled]);

	return (
		<>
			<div className="row repeat-field">
				<label>
					频率
					<select value={repeat.every} disabled={disabled} onChange={(e) => onChange(withEvery(repeat, e.target.value as Repeat["every"]))}>
						<option value="day">每天</option>
						<option value="week">每周</option>
						<option value="month">每月</option>
					</select>
				</label>
				{repeat.every === "week" && (
					<label>
						星期
						<select value={repeat.weekday} disabled={disabled} onChange={(e) => onChange({ ...repeat, weekday: e.target.value as Weekday })}>
							{WEEKDAY_NAMES.map(([value, name]) => (
								<option key={value} value={value}>
									{name}
								</option>
							))}
						</select>
					</label>
				)}
				{repeat.every === "month" && (
					<label>
						日期
						<select
							value={String(repeat.day)}
							disabled={disabled}
							onChange={(e) => onChange({ ...repeat, day: e.target.value === "last" ? "last" : Number(e.target.value) })}
						>
							{MONTH_DAYS.map((d) => (
								<option key={d} value={d}>
									{d} 号
								</option>
							))}
							<option value="last">最后一天</option>
						</select>
					</label>
				)}
				<label>
					时间
					<input type="time" value={repeat.at} disabled={disabled} onChange={(e) => e.target.value && onChange({ ...repeat, at: e.target.value })} />
				</label>
			</div>
			{slots && "error" in slots ? (
				<span className="banner error">{slots.error}</span>
			) : slots ? (
				<span className="hint">
					接下来：{slots.next.map((t) => when(t)).join(" · ")}（{slots.timeZone}）
				</span>
			) : null}
		</>
	);
}
